'use no memo'

import { useEffect, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { ProjectViewHeader } from './ProjectViewHeader'
import { ProjectViewRenderer } from './ProjectViewRenderer'
import { CyclesTab } from './cycles/CyclesTab'
import type { ProjectTabValue } from '../types'
import { DocsIndex } from '@/routes/features/pages/components/DocsIndex'
import { PagesEmptyState } from '@/routes/features/pages/components/PagesEmptyState'
import { FilterBar } from '@/components/features/filters'
import { useProjectPages } from '@/lib/db/rxdb/hooks'

interface ProjectTabContentProps {
  projectId: string
  projectKey: string
  workspaceSlug: string
  /** When rendered inside a space, clicking Views routes to the space-scoped URL. */
  spaceKey?: string
  onCycleClick?: (cycleId: string) => void
  onPageClick?: (slug: string) => void
}

/**
 * Shared tab content layout used by both ProjectLayout and SpaceProjectDetailsPage.
 * Renders: ProjectViewHeader tabs + FilterBar + tab content (Outlet / CyclesTab / DocsIndex).
 */
export function ProjectTabContent({
  projectId,
  projectKey,
  workspaceSlug,
  spaceKey,
  onCycleClick,
  onPageClick,
}: ProjectTabContentProps) {
  type BaseTab = Exclude<ProjectTabValue, 'views'>
  const BASE_TABS: Array<BaseTab> = ['tasks', 'pages', 'cycles']
  // Read tab from URL search so navigation between routes (e.g. coming back
  // from /views/$viewId with the tab param) lands on the right tab.
  const searchTab = useRouterState({
    select: (state) => {
      const search = state.location.search as Record<string, unknown> | undefined
      const raw = search?.projectTab as string | undefined
      if (raw && BASE_TABS.includes(raw as BaseTab)) return raw as BaseTab
      return undefined
    },
  })
  const [activeTab, setActiveTab] = useState<BaseTab>(searchTab ?? 'tasks')

  useEffect(() => {
    if (searchTab && searchTab !== activeTab) setActiveTab(searchTab)
  }, [searchTab, activeTab])

  const navigate = useNavigate()
  const { pages, isLoading: pagesLoading } = useProjectPages(projectId)
  const hasPages = (pages ?? []).some((p) => !p.isDeleted)

  const handleTabChange = (tab: ProjectTabValue) => {
    if (tab === 'views') {
      if (spaceKey) {
        navigate({
          to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views',
          params: { workspaceSlug, spaceKey, projectKey },
        })
      } else {
        navigate({
          to: '/$workspaceSlug/projects/$projectKey/views',
          params: { workspaceSlug, projectKey },
        })
      }
      return
    }
    setActiveTab(tab)
    // Persist tab in URL so refresh and back/forward keep the selection.
    if (spaceKey) {
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
        params: { workspaceSlug, spaceKey, projectKey },
        search: tab === 'tasks' ? {} : { projectTab: tab },
        replace: true,
      })
    } else {
      navigate({
        to: '/$workspaceSlug/projects/$projectKey',
        params: { workspaceSlug, projectKey },
        search: tab === 'tasks' ? {} : { projectTab: tab },
        replace: true,
      })
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Tabs Navigation */}
      <div className="flex-shrink-0">
        <ProjectViewHeader
          projectId={projectId}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* Filters */}
      {activeTab === 'tasks' && (
        <div className="flex-shrink-0">
          <FilterBar projectId={projectId} hiddenFields={['project']} />
        </div>
      )}

      {/* Tab Content — flex so TaskPageLayout flex-1 / h-full children bind height */}
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        {activeTab === 'tasks' ? (
          <ProjectViewRenderer projectId={projectId} hideProjectFilter />
        ) : activeTab === 'cycles' ? (
          <CyclesTab
            projectId={projectId}
            onCycleClick={onCycleClick}
          />
        ) : (
          <div className="h-full overflow-y-auto px-4 md:px-8 pt-8">
            {!pagesLoading && !hasPages ? (
              <PagesEmptyState
                projectId={projectId}
                projectKey={projectKey}
                workspaceSlug={workspaceSlug}
                navigateToPage={onPageClick}
              />
            ) : (
              <DocsIndex
                projectId={projectId}
                projectKey={projectKey}
                workspaceSlug={workspaceSlug}
                navigateToPage={onPageClick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
